import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  listSecurityEdgeAdvisories,
  listSecurityEdgeExposure,
  listSecurityVersionAdvisories,
  listSecurityVersions,
} from "../api/otaAdminApi";
import { formatDate } from "../utils/i18nFormat";

const COLS = [
  ["kev", "KEV", "#fb7185"],
  ["critical", "critical", "#f87171"],
  ["high", "high", "#fb923c"],
  ["medium", "medium", "#facc15"],
  ["low", "low", "#67e8f9"],
];
// KEV is a proper name; the other columns use the shared category names (admin.security.categories.*).
const colLabel = (t, key) => (key === "kev" ? "KEV" : t(`admin.security.categories.${key}`));
const PAGE = 50;

const box = { border: "1px solid rgba(148,163,184,.17)", borderRadius: 16, background: "#0b1b2d", padding: 20, marginTop: 14 };
const th = { textAlign: "left", padding: "8px 10px", color: "#6f8ba8", fontSize: 11, letterSpacing: ".08em", fontWeight: 800 };
const td = { padding: "9px 10px", borderTop: "1px solid rgba(148,163,184,.1)", color: "#dbe7f3", fontSize: 13 };
const tab = (on) => ({ padding: "6px 14px", borderRadius: 99, border: "1px solid rgba(148,163,184,.25)", background: on ? "rgba(14,67,87,.6)" : "transparent", color: "#e5eef8", cursor: "pointer" });

function Counts({ counts, dim }) {
  return COLS.map(([key, , color]) => (
    <td key={key} style={{ ...td, textAlign: "right", color: dim ? "#4b6179" : counts[key] ? color : "#6f8ba8", fontWeight: counts[key] ? 800 : 400 }}>
      {dim ? "—" : counts[key]}
    </td>
  ));
}

function AdvisoryList({ title, load, onClose }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cat, setCat] = useState("");
  const fetchPage = useCallback(
    async (offset) => {
      const res = await load({ limit: PAGE, offset, categories: cat || undefined });
      setTotal(res.total);
      setItems((prev) => (offset ? [...prev, ...res.items] : res.items));
    },
    [load, cat],
  );
  useEffect(() => {
    fetchPage(0).catch(() => toast.error(t("admin.security.exposure.advisoriesLoadFailed")));
  }, [fetchPage, t]);
  return (
    <div style={{ ...box, borderColor: "rgba(103,232,249,.3)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <strong style={{ color: "#f8fafc" }}>{t("admin.security.exposure.findings", { title, count: total })}</strong>
        <span style={{ display: "flex", gap: 8 }}>
          <select aria-label={t("admin.security.exposure.category")} value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">{t("admin.security.exposure.all")}</option>
            {COLS.map(([key]) => <option key={key} value={key}>{colLabel(t, key)}</option>)}
          </select>
          <button type="button" onClick={onClose}>{t("admin.security.exposure.close")}</button>
        </span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10 }}>
        <thead><tr><th style={th}>{t("admin.security.exposure.col.cve")}</th><th style={th}>{t("admin.security.exposure.col.category")}</th><th style={th}>{t("admin.security.exposure.col.component")}</th><th style={th}>{t("admin.security.exposure.col.description")}</th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id}>
              <td style={td}><code>{a.cve_id}</code></td>
              <td style={td}>{COLS.some(([k]) => k === a.category) ? colLabel(t, a.category) : a.category}</td>
              <td style={td}>{a.component_name || "—"}</td>
              <td style={td}>{a.title || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length < total && <button type="button" style={{ marginTop: 10 }} onClick={() => fetchPage(items.length)}>{t("admin.security.exposure.more", { count: total - items.length })}</button>}
    </div>
  );
}

/** Which OS / stack version and which device carries how many findings; click a row for its CVE list. */
export default function SecurityExposure({ accessToken }) {
  const { t } = useTranslation();
  const [view, setView] = useState("versions");
  const [versions, setVersions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null); // {title, load}

  useEffect(() => {
    if (view !== "versions") return;
    listSecurityVersions(accessToken).then(setVersions).catch(() => toast.error(t("admin.security.exposure.versionsLoadFailed")));
  }, [accessToken, view, t]);

  useEffect(() => {
    if (view !== "devices") return undefined;
    const timer = setTimeout(() => {
      listSecurityEdgeExposure(accessToken, { q: q.trim() || undefined, limit: 100 })
        .then((res) => setDevices(res.items))
        .catch(() => toast.error(t("admin.security.exposure.devicesLoadFailed")));
    }, 250);
    return () => clearTimeout(timer);
  }, [accessToken, view, q, t]);

  return (
    <section style={box} aria-label={t("admin.security.exposure.aria")}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <strong style={{ color: "#f8fafc", marginRight: 8 }}>{t("admin.security.exposure.title")}</strong>
        <button type="button" style={tab(view === "versions")} onClick={() => { setView("versions"); setOpen(null); }}>{t("admin.security.exposure.versions")}</button>
        <button type="button" style={tab(view === "devices")} onClick={() => { setView("devices"); setOpen(null); }}>{t("admin.security.exposure.devices")}</button>
        {view === "devices" && <input placeholder={t("admin.security.exposure.searchDevice")} value={q} onChange={(e) => setQ(e.target.value)} />}
      </div>

      {view === "versions" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>{t("admin.security.exposure.col.version")}</th><th style={th}>{t("admin.security.exposure.col.kind")}</th><th style={th}>{t("admin.security.exposure.col.scan")}</th>
              {COLS.map(([key]) => <th key={key} style={{ ...th, textAlign: "right" }}>{colLabel(t, key).toUpperCase()}</th>)}
              <th style={{ ...th, textAlign: "right" }}>{t("admin.security.exposure.col.devices")}</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => {
              const scanned = Boolean(v.scanned_at);
              return (
                <tr key={v.version} style={{ cursor: "pointer" }} onClick={() => setOpen({ title: v.version, load: (p) => listSecurityVersionAdvisories(accessToken, v.version, p) })}>
                  <td style={td}><code>{v.version}</code></td>
                  <td style={td}>{v.kind === "os" ? "OS" : "Stack"}</td>
                  <td style={td}>{scanned ? formatDate(v.scanned_at, { dateStyle: "short" }) : v.scan_status === "running" || v.scan_status === "queued" ? t("admin.security.exposure.scanRunning") : <em style={{ color: "#fbbf24" }}>{t("admin.security.exposure.notScanned")}</em>}</td>
                  <Counts counts={v.counts} dim={!scanned && v.counts.total === 0} />
                  <td style={{ ...td, textAlign: "right" }}>{v.devices}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {view === "devices" && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>{t("admin.security.exposure.col.device")}</th><th style={th}>OS</th><th style={th}>STACK</th>
              {COLS.map(([key]) => <th key={key} style={{ ...th, textAlign: "right" }}>{colLabel(t, key).toUpperCase()}</th>)}
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} style={{ cursor: "pointer" }} onClick={() => setOpen({ title: d.name, load: (p) => listSecurityEdgeAdvisories(accessToken, d.id, p) })}>
                <td style={td}>{d.name}</td>
                <td style={td}><code>{d.os_version || "—"}</code></td>
                <td style={td}><code>{d.stack_version || "—"}</code></td>
                <Counts counts={d.counts} />
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && <AdvisoryList key={open.title + view} title={open.title} load={open.load} onClose={() => setOpen(null)} />}
    </section>
  );
}
